import { type MetaArgs, type MetaFunction } from '@remix-run/node'
import { type ShouldRevalidateFunctionArgs } from '@remix-run/react'
import isEqual from 'lodash.isequal'
import uniqWith from 'lodash.uniqwith'

import { appConfig } from '#app/app-config.ts'
import { NEWSLETTER_FORM } from '#app/components/forms/newsletter-form/newsletter-form.tsx'
import { IMAGE_FILTERS } from '#app/components/ui/image'
import { type GetProduct } from '#app/models/ecommerce-providers/sylius/shop/fetch/product/get-product'
import { type Locale } from '#app/models/locale'
import {
	INDEX_I18NEXT,
	INDEX_ROUTE_LOADER,
} from '#app/routes/($lang)._index/route-config'
import { type loader } from '#app/routes/($lang)._index/server.route'
import { ROOT_INPUTS } from '#app/routes/_layout/route-config.ts'
import { type loader as RouteLoader } from '#app/routes/_layout/server.route.ts'
import { defaultMetaI18next } from '#app/utils/client/default-meta-i18next.ts'
import { imagePathFilter } from '#app/utils/image-path-filter.ts'

/* -------------------------------------------------------------------------------------------------
 * Handle
 * -----------------------------------------------------------------------------------------------*/

const handle = { i18n: [...Object.values(INDEX_I18NEXT)] }

/* -------------------------------------------------------------------------------------------------
 * Meta
 * -----------------------------------------------------------------------------------------------*/

const meta: MetaFunction<
	typeof loader,
	{
		[INDEX_ROUTE_LOADER.root]: typeof RouteLoader
	}
> = ({ data, matches }: MetaArgs) => {
	const parentMeta = matches.reduce((a, b) =>
		a.meta.length > b.meta.length ? a : b,
	).meta
	const title = parentMeta.find((meta) => 'title' in meta)
	const description = parentMeta.find(
		(meta) => 'name' in meta && meta.name === 'description',
	)

	const rootData = matches.find(
		(match) => match.id === INDEX_ROUTE_LOADER.root,
	)!.data as { locale: Locale }

	// @ts-ignore
	return uniqWith(
		title?.title.includes('Error')
			? [...parentMeta]
			: data
				? [
						...parentMeta.filter(
							(meta) =>
								!Object.entries(meta).find(
									([key, value]) =>
										key === 'title' ||
										(key === 'name' && value === 'description'),
								),
						),
						// @ts-ignore
						{ title: `${data.meta.title} | ${title?.title}` },
						{
							name: 'description',
							// @ts-ignore
							content: `${data.meta.description} | ${description?.content}`,
						},
						// Preload images
						// @ts-ignore
						...(
							data.products?.items?.filter(
								({ images }: GetProduct) => !!images?.[0]?.path,
							) || []
						).map(({ images }: GetProduct) => ({
							tagname: 'link',
							rel: 'preload',
							href: `${process.env.NODE_ENV === 'development' ? appConfig.url_dev_api : ''}${imagePathFilter({ path: images![0].path!, filter: IMAGE_FILTERS.gridFilter })}`,
							as: 'image',
						})),
						// I18next
						...defaultMetaI18next<typeof INDEX_I18NEXT>({
							I18NEXT: INDEX_I18NEXT,
							locale: rootData.locale,
						}),
					]
				: [
						...parentMeta.filter(
							(meta) =>
								!Object.entries(meta).find(
									([key, value]) =>
										key === 'title' ||
										(key === 'name' && value === 'description'),
								),
						),
						// @ts-ignore
						{ title: `Error | ${title?.title}` },
						{
							name: 'description',
							// @ts-ignore
							content: `Error | ${description?.content}`,
						},
					],
		(a, b) => isEqual(a, b),
	)
}

/* -------------------------------------------------------------------------------------------------
 * Should revalidate
 * -----------------------------------------------------------------------------------------------*/

const shouldRevalidate = ({
	formData,
	defaultShouldRevalidate,
}: ShouldRevalidateFunctionArgs) => {
	if (formData?.get(ROOT_INPUTS.subaction) === NEWSLETTER_FORM.subaction) {
		return false
	}

	return defaultShouldRevalidate
}

/* -----------------------------------------------------------------------------------------------*/

export { shouldRevalidate, handle, meta }
